/**
 * Global unit system — metric / imperial toggle.
 *
 * All display modules (elevation bar, measure tool, runway popups)
 * read from this module so a single toggle changes everything.
 *
 * metric:   m, km, m AGL
 * imperial: ft, mi, ft AGL
 */

export type UnitSystem = 'metric' | 'imperial';

let current: UnitSystem = 'metric';

/** Registered listeners — called whenever the unit changes. */
const listeners: Array<(system: UnitSystem) => void> = [];

export function getUnits(): UnitSystem {
  return current;
}

export function setUnits(system: UnitSystem): void {
  if (system === current) return;
  current = system;
  // Persist preference
  try { localStorage.setItem('adrone-units', system); } catch { /* noop */ }
  // Update toggle button text
  updateToggleLabel();
  // Notify listeners
  for (const fn of listeners) fn(current);
}

export function toggleUnits(): void {
  setUnits(current === 'metric' ? 'imperial' : 'metric');
}

/** Subscribe to unit changes. Returns unsubscribe function. */
export function onUnitsChange(fn: (system: UnitSystem) => void): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

// ── Formatting helpers ─────────────────────────────────────────────────────

/** Format an elevation in metres. Clamps small negatives to 0 (sea-level noise). */
export function formatElevation(metres: number): string {
  // Mapbox terrain data can return slightly negative values at sea level
  // due to geoid model differences. Clamp small negatives to 0.
  const clamped = metres < 0 && metres > -30 ? 0 : metres;
  if (current === 'imperial') {
    return `${Math.round(clamped * 3.28084)} ft`;
  }
  return `${Math.round(clamped)} m`;
}

/** Format a distance in kilometres. */
export function formatDistance(km: number): string {
  if (current === 'imperial') {
    const mi = km * 0.621371;
    return mi < 1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`;
  }
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`;
}

/** Format an altitude value (for AGL labels). Input in metres. */
export function formatAltitude(metres: number): string {
  if (current === 'imperial') {
    return `${Math.round(metres * 3.28084)} ft AGL`;
  }
  return `${metres} m AGL`;
}

// ── Toggle UI ──────────────────────────────────────────────────────────────

let toggleEl: HTMLButtonElement | null = null;

function updateToggleLabel(): void {
  if (toggleEl) {
    toggleEl.textContent = current === 'metric' ? 'METRIC' : 'IMPERIAL';
  }
}

/**
 * Create the small unit toggle button and append it near the elevation bar.
 * Called once at startup.
 */
export function createUnitToggle(): void {
  // Restore saved preference
  try {
    const saved = localStorage.getItem('adrone-units');
    if (saved === 'imperial' || saved === 'metric') current = saved;
  } catch { /* noop */ }

  toggleEl = document.createElement('button');
  toggleEl.id = 'unit-toggle';
  toggleEl.textContent = current === 'metric' ? 'METRIC' : 'IMPERIAL';
  toggleEl.title = 'Toggle metric / imperial units';
  toggleEl.addEventListener('click', () => toggleUnits());

  // Insert into the map container area
  const mapEl = document.getElementById('map');
  if (mapEl) {
    mapEl.appendChild(toggleEl);
  } else {
    document.body.appendChild(toggleEl);
  }
}
